import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Divider,
  Skeleton,
  alpha,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import { showToast, WEBSERVICE, getDashBoardInfo } from "../../CoreDataService";
import InfoPopUp from "../../Common/InfoPopUp";
import { ContainedButton } from "../../Common/Buttons";

export default function BankDetails() {
  const navigate = useNavigate();
  const { btncolor } = useSelector((state) => state);

  const uuid = localStorage.getItem("uuid");
  const token = localStorage.getItem("access_token");
  const userId = localStorage.getItem("userId");

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [statusData, setStatusData] = useState({});
  const [userFullName, setUserFullName] = useState("");
  const [userCompanyName, setUserCompanyName] = useState("");

  // New state for handling file uploads
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingDocUrl, setExistingDocUrl] = useState("");

  const {
    control,
    reset,
    watch,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      accountHolder: "",
      ibanNumber: "",
      bankName: "",
      accountType: "",
      bankAddress: "",
      codeType: "ROUTING NO",
      additionalCode: "",
      bankVerificationDocType: "Voided Check", // Set fallback value matching layout
    },
  });

  /* ======================
      Validators
  ====================== */

  const validateBankCode = (code) => {
    if (!code) return "Required";
    const type = watch("codeType");

    if (type === "ROUTING NO")
      return /^\d{6,9}$/.test(code) || "Invalid Routing Number (6–9 digits)";

    if (type === "SWIFT CODE")
      return (
        /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(code) ||
        "Invalid SWIFT Code"
      );

    if (type === "IFSC CODE")
      return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(code) || "Invalid IFSC Code";

    return "Invalid Code";
  };

  /* ======================
      Load Data
  ====================== */

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [userRes, bankRes, dash] = await Promise.all([
          axios.post(
            `${WEBSERVICE}user/GetUserDetails`,
            { userId, uuid },
            { headers: { Authorization: `BEARER ${token}` } }
          ),
          axios.post(
            `${WEBSERVICE}/user/GetUserBankDetails`,
            { uuid },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `BEARER ${token}`,
              },
            }
          ),
          getDashBoardInfo(),
        ]);

        const user = userRes.data.userResult;
        setUserDetails(user);

        const fullName = `${user.firstName} ${user.lastName}`;
        setUserFullName(fullName);
        setUserCompanyName(user?.enterpriseUser?.companyName || "");

        setStatusData(dash);

        const bank = bankRes.data.bankDetails || {};
        if (bank.bankVerificationDoc) {
          setExistingDocUrl(bank.bankVerificationDoc);
        }

        let accType = bank.accountType || "Personal Savings";
        if (accType === "Personal Current") {
          accType = "Personal Checking";
        } else if (accType === "Business Current") {
          accType = "Business Checking";
        }

        reset({
          accountHolder: user?.userType === 1 ? fullName : (user?.enterpriseUser?.companyName || fullName),
          ibanNumber: bank.account_no || "",
          bankName: bank.bank_name || "",
          accountType: accType,
          bankAddress: bank.bankAddress || "",
          codeType: bank.routing_no
            ? "ROUTING NO"
            : bank.swiftCode
            ? "SWIFT CODE"
            : "IFSC CODE",
          additionalCode:
            bank.routing_no || bank.swiftCode || bank.ifscCode || "",
          bankVerificationDocType: bank.bankVerificationDocType || "Voided Check",
        });
      } catch (e) {
        showToast("error", "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [reset, token, uuid, userId]);

  /* ======================
      File Input Interception
  ====================== */

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size limit: Max 5MB
    if (file.size > 5 * 1024 * 1024) {
      showToast("error", "File size exceeds 5 MB");
      return;
    }

    // Check file format
    const acceptableFormats = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!acceptableFormats.includes(file.type)) {
      showToast("error", "Unsupported file format. Please upload PDF, JPG, or PNG.");
      return;
    }

    setSelectedFile(file);
  };

  /* ======================
      Submit
  ====================== */

  const yesConfirm = () => {
    const data = watch();
    if (Object.values(data).some((v) => !v)) {
      showToast("warning", "All fields are mandatory.");
      return;
    }

    // Restrict submitting without uploading document
    if (!selectedFile && !existingDocUrl) {
      showToast("warning", "Please upload a bank verification document.");
      return;
    }

    setModalOpen(true);
  };

  const updateBankDetails = async () => {
    const data = watch();

    const bankData = {
      uuid,
      benificiary_name: data.accountHolder,
      bank_name: data.bankName,
      account_no: data.ibanNumber,
      bankAddress: data.bankAddress,
      accountType: data.accountType,
      country: userDetails?.country || "",
      bankVerificationDocType: data.bankVerificationDocType, // Added inside bankData
    };

    if (data.codeType === "ROUTING NO") bankData.routing_no = data.additionalCode;
    if (data.codeType === "SWIFT CODE") bankData.swiftCode = data.additionalCode;
    if (data.codeType === "IFSC CODE") bankData.ifscCode = data.additionalCode;

    const formData = new FormData();
    formData.append("bankData", JSON.stringify(bankData));
    
    // Appending document object as "doc"
    if (selectedFile) {
      formData.append("doc", selectedFile);
    }

    try {
      const res = await axios.post(
        `${WEBSERVICE}/user/UpdateUserBankDetails-v2`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (res.data.error.error_data !== 0)
        showToast("error", res.data.error.error_msg);
      else showToast("success", "Bank Details Updated");
    } catch {
      showToast("error", "Update failed");
    } finally {
      setModalOpen(false);
    }
  };

  /* ======================
      Loader
  ====================== */

  if (loading) {
    return (
      <Box sx={pageWrapper}>
        <Skeleton
          variant="rounded"
          height={120}
          sx={{ borderRadius: "18px", mb: 3 }}
        />
        <Skeleton
          variant="rounded"
          height={420}
          sx={{ borderRadius: "16px" }}
        />
      </Box>
    );
  }

  /* ======================
      Render
  ====================== */

  return (
    <Box sx={pageWrapper}>
      <InfoPopUp
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        title="Bank Details"
        description="Submit your own Bank details. When you make a withdrawal, the amount will be credited to the provided bank account."
      />

      {/* ── Hero Banner ── */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <AccountBalanceOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={heroTitle}>Bank Details</Typography>
            <Typography sx={heroSubtitle}>
              Add or update your bank account information for seamless withdrawals
            </Typography>
          </Box>
          <IconButton
            onClick={() => setInfoModalOpen(true)}
            sx={heroInfoBtn}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Main Content ── */}
      {userDetails?.userType !== 0 ? (
        <Box sx={mainCard}>
          {/* Card header */}
          <Box sx={cardHeader}>
            <Box sx={cardHeaderLeft}>
              <AccountBalanceOutlinedIcon
                sx={{ fontSize: 18, color: btncolor }}
              />
              <Typography sx={cardHeaderText}>Account Information</Typography>
            </Box>
            {statusData.bankDetailsStatus && (
              <Box sx={statusChip}>
                <Box
                  sx={{
                    ...statusDot,
                    backgroundColor: "#16a34a",
                  }}
                />
                <Typography sx={statusChipText}>
                  {statusData.bankDetailsStatus}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: "rgba(125,125,125,0.12)" }} />

          {/* Form */}
          <Box sx={formBody}>
            <Box sx={formGrid}>
              {/* Account Holder */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>Account Holder Name</Typography>
                <Controller
                  name="accountHolder"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Account holder name"
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={modernFieldStyle}
                    />
                  )}
                />
              </Box>

              {/* IBAN */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>IBAN / Account Number</Typography>
                <Controller
                  name="ibanNumber"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      placeholder="Enter IBAN or account number"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      sx={modernFieldStyle}
                    />
                  )}
                />
              </Box>

              {/* Account Type */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>Account Type</Typography>
                <Controller
                  name="accountType"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      sx={modernFieldStyle}
                    >
                      <MenuItem value="Personal Savings">Personal Savings</MenuItem>
                      <MenuItem value="Personal Checking">Personal Checking</MenuItem>
                      <MenuItem value="Business Savings">Business Savings</MenuItem>
                      <MenuItem value="Business Checking">Business Checking</MenuItem>
                    </TextField>
                  )}
                />
              </Box>

              {/* Bank Name */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>Bank Name</Typography>
                <Controller
                  name="bankName"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      placeholder="Enter bank name"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      sx={modernFieldStyle}
                    />
                  )}
                />
              </Box>

              {/* Code Type + Code — side by side */}
              <Box sx={codeRow}>
                <Box sx={{ ...fieldWrap, minWidth: 0, flex: "0 0 200px" }}>
                  <Typography sx={fieldLabel}>Code Type</Typography>
                  <Controller
                    name="codeType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        onChange={(e) => {
                          field.onChange(e);
                          reset({ ...watch(), additionalCode: "" });
                        }}
                        sx={modernFieldStyle}
                      >
                        <MenuItem value="ROUTING NO">ROUTING NO</MenuItem>
                        <MenuItem value="IFSC CODE">IFSC CODE</MenuItem>
                        <MenuItem value="SWIFT CODE">SWIFT CODE</MenuItem>
                      </TextField>
                    )}
                  />
                </Box>
                <Box sx={{ ...fieldWrap, flex: 1, minWidth: 0 }}>
                  <Typography sx={fieldLabel}>Code Value</Typography>
                  <Controller
                    name="additionalCode"
                    control={control}
                    rules={{ validate: validateBankCode }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        placeholder={`Enter ${watch("codeType")}`}
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        sx={modernFieldStyle}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Bank Address — full width */}
              <Box sx={{ ...fieldWrap, gridColumn: "1 / -1" }}>
                <Typography sx={fieldLabel}>Bank Address</Typography>
                <Controller
                  name="bankAddress"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      placeholder="Enter bank address"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      sx={modernFieldStyle}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* ── NEW SECTION: Bank Verification Document ── */}
            <Box sx={docSectionWrapper}>
              <Box sx={docSectionHeader}>
                <DescriptionOutlinedIcon sx={{ fontSize: 20, color: btncolor || "#1976d2" }} />
                <Box>
                  <Typography sx={docSectionTitle}>Bank Verification Document</Typography>
                  <Typography sx={docSectionSubtitle}>Upload a document to verify your bank account</Typography>
                </Box>
              </Box>

              <Typography sx={docInstructionsTitle}>PLEASE UPLOAD ONE OF THE FOLLOWING:</Typography>
              
              <Box sx={instructionsGrid}>
                <Box className="instruction-item">Voided Check</Box>
                <Box className="instruction-item">Bank Letter / Bank Account Verification Letter</Box>
                <Box className="instruction-item">Recent Bank Statement (last 3 months)</Box>
                <Box className="instruction-item">Direct Deposit Form / ACH Authorization Form</Box>
                <Box className="instruction-item">Screenshot or PDF from Online Banking</Box>
              </Box>

              {/* Requirements Callout */}
              <Box sx={reqCalloutBox}>
                <Box sx={reqCalloutHeader}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Uploaded document should show:</Typography>
                </Box>
                <Box component="ul" sx={reqList}>
                  <li>Account Holder Name</li>
                  <li>Bank Name</li>
                  <li>Account Number (partially masked if needed)</li>
                  <li>Routing / IFSC / SWIFT details (as applicable)</li>
                </Box>
              </Box>

              {/* Document Picker Field and Drag-and-Drop Form Inputs */}
              <Box sx={uploadFieldsGrid}>
                <Box sx={fieldWrap}>
                  <Typography sx={fieldLabel}>Bank Account Verification Document Type *</Typography>
                  <Controller
                    name="bankVerificationDocType"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        sx={modernFieldStyle}
                      >
                        <MenuItem value="Voided Check">Voided Check</MenuItem>
                        <MenuItem value="Bank Letter">Bank Letter / Bank Account Verification Letter</MenuItem>
                        <MenuItem value="Recent Bank Statement">Recent Bank Statement (last 3 months)</MenuItem>
                        <MenuItem value="Direct Deposit Form">Direct Deposit Form / ACH Authorization Form</MenuItem>
                        <MenuItem value="Online Banking Screenshot">Screenshot or PDF from Online Banking</MenuItem>
                      </TextField>
                    )}
                  />
                </Box>

                <Box sx={fieldWrap}>
                  <Typography sx={fieldLabel}>Upload Bank Verification Document *</Typography>
                  <Box 
                    component="label" 
                    sx={{
                      ...dropZoneStyle,
                      borderColor: selectedFile ? (btncolor || "#1976d2") : "rgba(125,125,125,0.25)"
                    }}
                  >
                    <input 
                      type="file" 
                      accept=".pdf,.jpg,.jpeg,.png" 
                      hidden 
                      onChange={handleFileChange} 
                    />
                    <CloudUploadOutlinedIcon sx={{ fontSize: 28, opacity: 0.7, mb: 0.5, color: btncolor }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      {selectedFile ? selectedFile.name : "Click or drag & drop to upload"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", opacity: 0.5, mt: 0.25 }}>
                      Accepted formats: PDF, JPG, PNG (Max 5 MB)
                    </Typography>
                  </Box>
                  {existingDocUrl && !selectedFile && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: "0.8rem", color: btncolor }}>
                        Current document:{" "}
                        <a 
                          href={existingDocUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ color: "inherit", textDecoration: "underline", fontWeight: 500 }}
                        >
                          View Document
                        </a>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Notice strip */}
            <Box sx={noticeStrip}>
              <LockOutlinedIcon sx={{ fontSize: 15, color: btncolor, flexShrink: 0, mt: "1px" }} />
              <Typography sx={noticeText}>
                Your bank details are securely encrypted. Updating these details may temporarily restrict certain features while we verify the new information.
              </Typography>
            </Box>

            {/* Save button */}
            <Box sx={formFooter}>
              <ContainedButton
                disabled={!isValid || (!selectedFile && !existingDocUrl)}
                onClick={yesConfirm}
                sx={saveBtn}
              >
                Save Changes
              </ContainedButton>
            </Box>
          </Box>
        </Box>
      ) : (
        /* ── Identity verification gate ── */
        <Box sx={gateCard}>
          <Box sx={gateIconWrap}>
            <VerifiedUserOutlinedIcon
              sx={{ fontSize: 48, color: btncolor, opacity: 0.85 }}
            />
          </Box>
          <Typography sx={gateTitle}>Identity Verification Required</Typography>
          <Typography sx={gateSubtitle}>
            Kindly complete your identity verification before proceeding to add your bank information.
          </Typography>
          <ContainedButton
            sx={{ mt: 3, px: 4, height: 44, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
            onClick={() => navigate("/identity-verification")}
          >
            Go to Identity Verification
          </ContainedButton>
        </Box>
      )}

      {/* ── Confirm Dialog ── */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>Confirm Update</DialogTitle>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogContent sx={dialogContentStyle}>
          <Typography sx={dialogText}>
            Editing these details will restrict some features temporarily while your new bank information is being verified.
          </Typography>
        </DialogContent>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogActions sx={dialogActionsStyle}>
          <ContainedButton
            sx={{ mx: 1, minWidth: 110, height: 40, borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </ContainedButton>
          <ContainedButton
            sx={{ mx: 1, minWidth: 110, height: 40, borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
            onClick={updateBankDetails}
          >
            Confirm
          </ContainedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Styles (mirrors Report / Convert / Earn design system) ────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

// ── Hero ─────────────────────────────────────────────────────────────────────

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 600,
};

const heroInfoBtn = {
  ml: "auto",
  flexShrink: 0,
  backgroundColor: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.22)",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
};

// ── Main Card ─────────────────────────────────────────────────────────────────

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: { xs: 2, md: 3 },
  py: 2,
  backgroundColor: "var(--body-color-less2)",
  flexWrap: "wrap",
  gap: 1,
};

const cardHeaderLeft = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const cardHeaderText = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--text-color)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const statusChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.6,
  backgroundColor: "rgba(22,163,74,0.1)",
  border: "1px solid rgba(22,163,74,0.25)",
  borderRadius: "20px",
  px: 1.25,
  py: 0.35,
};

const statusDot = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  flexShrink: 0,
};

const statusChipText = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#16a34a",
};

// ── Form ──────────────────────────────────────────────────────────────────────

const formBody = {
  p: { xs: 2, md: 3 },
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2,
  mb: 2.5,
};

const fieldWrap = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  minWidth: 0,
};

const fieldLabel = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "var(--text-color)",
  opacity: 0.7,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const codeRow = {
  display: "flex",
  gap: 1.5,
  alignItems: "flex-end",
  gridColumn: "1 / -1",
  flexWrap: { xs: "wrap", sm: "nowrap" },
};

const modernFieldStyle = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiSelect-select": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.7 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "var(--text-color)",
    WebkitTextFillColor: "var(--text-color)",
    opacity: 0.5,
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.72rem",
    mt: 0.5,
  },
};

const noticeStrip = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "10px",
  px: 2,
  py: 1.5,
  mb: 3,
  border: "1px solid rgba(125,125,125,0.12)",
};

const noticeText = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.65,
  lineHeight: 1.6,
};

const formFooter = {
  display: "flex",
  justifyContent: "flex-end",
};

const saveBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 600,
  fontSize: "0.88rem",
  textTransform: "none",
  letterSpacing: "0.01em",
  minWidth: 160,
};

// ── New Document Upload Styles ────────────────────────────────────────────────

const docSectionWrapper = {
  mt: 4,
  mb: 4,
  pt: 3,
  borderTop: "1px dashed rgba(125,125,125,0.2)",
};

const docSectionHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 3,
};

const docSectionTitle = {
  fontSize: "1.05rem",
  fontWeight: 700,
  color: "var(--text-color)",
};

const docSectionSubtitle = {
  fontSize: "0.8rem",
  opacity: 0.6,
  color: "var(--text-color)",
};

const docInstructionsTitle = {
  fontSize: "0.78rem",
  fontWeight: 700,
  color: "var(--text-color)",
  letterSpacing: "0.03em",
  mb: 1.5,
};

const instructionsGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gap: 1.5,
  mb: 3,
  "& .instruction-item": {
    fontSize: "0.82rem",
    color: "var(--text-color)",
    opacity: 0.85,
    display: "flex",
    alignItems: "center",
    gap: 1,
    "&::before": {
      content: '"•"',
      color: "#3b82f6", // Adjust bullet color to match your layout accent
      fontWeight: "bold",
      fontSize: "1.2rem",
    }
  }
};

const reqCalloutBox = {
  backgroundColor: "rgba(217, 119, 6, 0.05)",
  border: "1px solid rgba(217, 119, 6, 0.15)",
  borderRadius: "10px",
  p: 2,
  mb: 3.5,
};

const reqCalloutHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1,
  "& p": {
    fontSize: "0.82rem",
    color: "var(--text-color)",
  },
  "& svg": {
    color: "rgba(217,119,6,0.85)"
  }
};

const reqList = {
  margin: 0,
  paddingLeft: "20px",
  "& li": {
    fontSize: "0.78rem",
    color: "var(--text-color)",
    opacity: 0.75,
    lineHeight: 1.7,
  }
};

const uploadFieldsGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2.5,
  mb: 1,
};

const dropZoneStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "110px",
  borderRadius: "10px",
  backgroundColor: "var(--body-color)",
  border: "2px dashed",
  cursor: "pointer",
  textAlign: "center",
  px: 2,
  transition: "border-color 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(125,125,125,0.05)",
  }
};

// ── Identity Gate ─────────────────────────────────────────────────────────────

const gateCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  py: { xs: 6, md: 9 },
  px: { xs: 3, md: 6 },
};

const gateIconWrap = {
  width: 80,
  height: 80,
  borderRadius: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "var(--body-color-less2)",
  border: "1px solid rgba(125,125,125,0.15)",
  mb: 2.5,
};

const gateTitle = {
  fontSize: { xs: "1.15rem", md: "1.35rem" },
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
  mb: 1,
};

const gateSubtitle = {
  fontSize: "0.88rem",
  color: "var(--text-color)",
  opacity: 0.6,
  lineHeight: 1.65,
  maxWidth: 460,
};

// ── Dialog ────────────────────────────────────────────────────────────────────

const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 420 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.1rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
};

const dialogContentStyle = {
  pt: 3,
  pb: 2,
  px: 3,
  textAlign: "center",
  backgroundColor: "var(--popupbody-color)",
};

const dialogText = {
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.8,
  lineHeight: 1.65,
};

const dialogActionsStyle = {
  justifyContent: "center",
  pb: 3,
  px: 3,
  backgroundColor: "var(--popupbody-color)",
};